import type { SectionDefinition } from './types';
import { ComplimentaryCareEnrollmentSection } from './complimentary-care/ComplimentaryCareEnrollmentSection';
import { ComplimentaryCareReviewSection } from './complimentary-care-review/ComplimentaryCareReviewSection';
import { EnrollmentDetailPage } from './complimentary-care-review/EnrollmentDetailPage';
import { CtInterviewReviewSection } from './ct-interview-review/CtInterviewReviewSection';
import { SubmissionDetailPage } from './ct-interview-review/SubmissionDetailPage';
import { ObservationAgreementReviewSection } from './observation-agreement-review/ObservationAgreementReviewSection';
import { SignatureDetailPage } from './observation-agreement-review/SignatureDetailPage';
import { EmployeeReferralSection } from './employee-referral/EmployeeReferralSection';
import { ReferralJobTitlesSection } from './employee-referral/ReferralJobTitlesSection';
import { ReferralPayoutDatesSection } from './employee-referral/ReferralPayoutDatesSection';
import { OnboardingEmailSection } from './onboarding-email/OnboardingEmailSection';
import { PublicFormsSection } from './public-forms/PublicFormsSection';
import { CtInterviewMaterialsSection, CtResourcesSection } from './ct-resources/CtResourcesSection';

/**
 * Every section hosted in the hub. This list is the whole registration step — a new section is its
 * own folder under `sections/` plus one entry here. `id` must match the backend module's own section
 * id, since that's what `GET /api/{id}/access` is keyed by.
 */
export const sections: SectionDefinition[] = [
  {
    id: 'onboarding-email',
    basePath: '/onboarding-email',
    navLabel: 'Manager Onboarding Email',
    description: 'Fill in one form and get the new-hire welcome email as a draft in your Outlook mailbox.',
    category: 'HR',
    pages: [{ id: 'form', path: '', navLabel: 'Manager Onboarding Email', element: <OnboardingEmailSection /> }],
  },
  {
    id: 'employee-referral',
    basePath: '/employee-referral',
    navLabel: 'Employee Referral',
    description:
      'Track who referred whom, what the bonus is worth, and which payout date it falls due on.',
    category: 'HR',
    pages: [
      { id: 'referrals', path: '', navLabel: 'Referrals', element: <EmployeeReferralSection /> },
      {
        id: 'job-titles',
        path: 'job-titles',
        navLabel: 'Job titles and payouts',
        element: <ReferralJobTitlesSection />,
      },
      {
        id: 'payout-dates',
        path: 'payout-dates',
        navLabel: 'Payout dates',
        element: <ReferralPayoutDatesSection />,
      },
    ],
  },
  {
    id: 'complimentary-care',
    basePath: '/complimentary-care',
    navLabel: 'Complimentary Chiropractic Care',
    description: 'Enroll yourself, your spouse or domestic partner, and your children in complimentary chiropractic care.',
    category: null,
    pages: [{ id: 'form', path: '', navLabel: 'Complimentary Chiropractic Care', element: <ComplimentaryCareEnrollmentSection /> }],
  },
  {
    id: 'complimentary-care-review',
    basePath: '/complimentary-care-review',
    navLabel: 'Chiropractic Care Enrollments',
    description: 'Every submitted complimentary chiropractic care enrollment in one place.',
    category: 'HR',
    pages: [
      { id: 'review', path: '', navLabel: 'Chiropractic Care Enrollments', element: <ComplimentaryCareReviewSection /> },
      {
        id: 'detail',
        path: ':enrollmentId',
        navLabel: 'Enrollment',
        element: <EnrollmentDetailPage />,
        // Reached by opening a row, so it stays out of the sidebar.
        hidden: true,
      },
    ],
  },
  {
    // The hand-out page for the forms that need no sign-in. `clientOnly` because it has no backend
    // module — see SectionDefinition for why that flag has to exist rather than being inferred.
    //
    // Its path is `/public-forms`, which is NOT under the `/public/` routing prefix and so is not
    // itself public. See `public/publicRoutes.ts`. The path keeps its old spelling though the label
    // has changed: it is what anybody who bookmarked this page has.
    //
    // Under Recruiting, and first within it, since both forms it hands out are recruiting's. It used
    // to sit uncategorized above every category; being next to the two dashboards that hold what
    // comes back on these forms is what makes the pair of them read as one process.
    id: 'public-forms',
    basePath: '/public-forms',
    navLabel: 'CT Interview Forms (Public)',
    description: 'Links to the forms that need no sign-in, to open or to send to someone.',
    category: 'Recruiting',
    clientOnly: true,
    pages: [
      { id: 'links', path: '', navLabel: 'CT Interview Forms (Public)', element: <PublicFormsSection /> },
    ],
  },
  {
    // The interview packet and clinic visit agenda, moved out of CT Resources into a section of
    // their own. Same page, same API module and same settings as CT Resources — the entries keep
    // their ids, so URLs and switches already set against them carry over. Its group falls back to
    // CT Resources' until `SectionAccess:RequiredGroupId:ct-interview-materials` is set.
    //
    // Straight after the public forms: the forms and these materials are what an interview is run
    // with, so they sit together ahead of the dashboards that read what comes back.
    id: 'ct-interview-materials',
    basePath: '/ct-interview-materials',
    navLabel: 'CT Interview Materials',
    description: 'The Clinical Team interview packet and clinic visit agenda.',
    category: 'Recruiting',
    pages: [
      { id: 'links', path: '', navLabel: 'CT Interview Materials', element: <CtInterviewMaterialsSection /> },
    ],
  },
  {
    // The read side of the CT interview section. Its other half — the candidate form — is public
    // and deliberately not registered here: it is served outside the hub shell entirely, from
    // `src/public/`, because a job candidate has no account to sign in with.
    id: 'ct-interview-review',
    basePath: '/ct-interview-review',
    navLabel: 'Completed CT Candidate Assessments',
    description: 'Read the skills assessments candidates have handed in at the clinics.',
    category: 'Recruiting',
    pages: [
      {
        id: 'list',
        path: '',
        navLabel: 'Completed CT Candidate Assessments',
        element: <CtInterviewReviewSection />,
      },
      {
        id: 'detail',
        path: ':submissionId',
        navLabel: 'Assessment',
        element: <SubmissionDetailPage />,
        hidden: true,
      },
    ],
  },
  {
    // The read side of the observation agreement section. Its other half — the signing page — is
    // public and deliberately not registered here, for the same reason the CT candidate form is
    // not: the person signing has no account to sign in with.
    id: 'observation-agreement-review',
    basePath: '/observation-agreement-review',
    navLabel: 'Completed Office Observation Agreements',
    description:
      'Every signed job-shadowing agreement, exactly as the person who signed it was shown it.',
    category: 'Recruiting',
    pages: [
      {
        id: 'list',
        path: '',
        navLabel: 'Completed Office Observation Agreements',
        element: <ObservationAgreementReviewSection />,
      },
      {
        id: 'detail',
        path: ':signatureId',
        navLabel: 'Signed agreement',
        element: <SignatureDetailPage />,
        hidden: true,
      },
    ],
  },
  {
    // Reference documents, not a form and not a dashboard: it holds no data of its own and reaches
    // no table. Each link's URL is API configuration rather than anything in this file — see
    // `CtResourcesOptions` — so a document that does not exist yet is one setting away from live.
    //
    // Not `clientOnly`, unlike the public forms entry above, though both are lists of links. These
    // are internal documents and the list is served from behind the section's group; the other page
    // lists URLs that are public by definition and has nothing to gate.
    id: 'ct-resources',
    basePath: '/ct-resources',
    navLabel: 'CT Resources',
    description: 'The Clinical Team SOP and intake forms.',
    category: 'Recruiting',
    pages: [{ id: 'links', path: '', navLabel: 'CT Resources', element: <CtResourcesSection /> }],
  },
];
